import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useStatus } from "@powersync/react";
import { PMButton, PMCard, PMDataTable, PMSegmented, PMSettingsLayout, PMSyncIndicator, PMAvatar } from "@/ds/vendor";
import dsVersion from "@/ds/vendor/VERSION?raw";
import { useApp } from "@/app/AppContext";
import { usePeople } from "@/data/queries";
import { applyTheme, getTheme, type Theme } from "@/ui/theme";
import { avatarColor, fullName, plural, relTime } from "@/ui/format";
import { Page } from "@/ui/Page";
import "./settings.css";

const SECTIONS = [
  { label: "Account", items: [{ id: "profile", label: "Profile", icon: "user-outline" }, { id: "appearance", label: "Appearance", icon: "sliders-outline" }] },
  { label: "Team", items: [{ id: "team", label: "Team", icon: "home-outline" }, { id: "members", label: "Members", icon: "user-add-outline" }, { id: "hierarchy", label: "Level hierarchy", icon: "layout-outline" }] },
  { label: "System", items: [{ id: "sync", label: "Data & sync", icon: "sync-filled" }, { id: "about", label: "About", icon: "document-outline" }] },
];
const CLASSIC = import.meta.env.VITE_API_ORIGIN || "https://dev.propelmapper.com";

function getFont(): string { try { return localStorage.getItem("pm.font") || "outfit"; } catch { return "outfit"; } }
function applyFont(f: string) {
  if (f === "system") document.documentElement.dataset.font = "system"; else delete document.documentElement.dataset.font;
  try { localStorage.setItem("pm.font", f); } catch { /* ignore */ }
}

export function SettingsPage() {
  const search = useSearch({ from: "/t/$teamId/settings" });
  const nav = useNavigate();
  const section = search.section || "profile";
  return (
    <Page chip="BUSINESS" title="Settings">
      <PMSettingsLayout sections={SECTIONS} active={section} onSelect={(id: string) => nav({ to: ".", search: { section: id }, replace: true } as any)}>
        <div className="settings-pane">
          {section === "profile" && <Profile />}
          {section === "appearance" && <Appearance />}
          {section === "team" && <TeamSection />}
          {section === "members" && <Members />}
          {section === "hierarchy" && <Hierarchy />}
          {section === "sync" && <Sync />}
          {section === "about" && <About />}
        </div>
      </PMSettingsLayout>
    </Page>
  );
}

function Profile() {
  const { userEmail, signOut } = useApp();
  return (
    <PMCard eyebrow="Profile">
      <div className="set-row"><PMAvatar name={userEmail} size={48} /><div><div className="list-title">{userEmail}</div><div className="hint">Signed in on this device</div></div></div>
      <div className="set-actions"><PMButton variant="end" onClick={signOut}>Sign out</PMButton></div>
    </PMCard>
  );
}

function Appearance() {
  const [theme, setTheme] = useState<Theme>(getTheme());
  const [font, setFont] = useState(getFont());
  return (
    <PMCard eyebrow="Appearance">
      <div className="set-field"><div><div className="set-label">Theme</div><div className="hint">Auto follows the system setting.</div></div>
        <PMSegmented value={theme} onChange={(v: string) => { setTheme(v as Theme); applyTheme(v as Theme); }} options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }, { value: "system", label: "Auto" }]} /></div>
      <div className="set-field"><div><div className="set-label">Font style</div><div className="hint">Outfit is the PropelMapper typeface. System uses the device font.</div></div>
        <PMSegmented value={font} onChange={(v: string) => { setFont(v); applyFont(v); }} options={[{ value: "outfit", label: "Outfit" }, { value: "system", label: "System" }]} /></div>
    </PMCard>
  );
}

function TeamSection() {
  const { teamName, hierarchy } = useApp();
  const people = usePeople();
  return (
    <PMCard eyebrow="Team">
      <dl className="set-dl">
        <dt>Name</dt><dd>{teamName}</dd>
        <dt>Members</dt><dd>{plural(people.data.length, "member")}</dd>
        <dt>Hierarchy</dt><dd>{hierarchy.label}</dd>
      </dl>
      <p className="hint">Report templates, taxonomy and billing are managed in classic for now. <a href={CLASSIC} target="_blank" rel="noopener">Open in classic</a></p>
    </PMCard>
  );
}

function Members() {
  const people = usePeople();
  return (
    <PMCard eyebrow="Members" count={people.data.length}>
      <PMDataTable rowKey="user_id" rows={people.data} emptyText="No members synced"
        columns={[
          { key: "name", label: "Name", sortable: true, sortValue: (p: any) => fullName(p), render: (p: any) => <span className="row"><PMAvatar name={fullName(p)} size={28} color={avatarColor(p.user_id)} />{fullName(p)}</span> },
          { key: "email", label: "Email", sortable: true },
        ]} />
      <p className="hint">Invites and roles are managed in classic for now. <a href={CLASSIC} target="_blank" rel="noopener">Open in classic</a></p>
    </PMCard>
  );
}

function Hierarchy() {
  const { hierarchy } = useApp();
  return (
    <PMCard eyebrow="Level hierarchy">
      <p className="hint">Every breadcrumb, picker and label uses this team's wording.</p>
      <div className="lvl-row">
        {hierarchy.levels.map((l, i) => (
          <span key={l.key} className="lvl-wrap">
            <span className="lvl">{l.emoji} {l.label}</span>
            {i < hierarchy.levels.length - 1 && <span className="lvl-arrow" aria-hidden="true">→</span>}
          </span>
        ))}
      </div>
      <p className="hint">{hierarchy.label}. Skip-level linking is allowed. Set at team provisioning.</p>
    </PMCard>
  );
}

function Sync() {
  const { mode } = useApp();
  const s = useStatus();
  const state = mode === "demo" ? "synced" : !s.connected ? (s.hasSynced ? "offline" : "syncing") : s.dataFlowStatus?.uploading ? "uploading" : s.dataFlowStatus?.downloading ? "downloading" : "synced";
  return (
    <PMCard eyebrow="Data & sync">
      <dl className="set-dl">
        <dt>Mode</dt><dd>{mode === "demo" ? "Demo team (local only, never syncs)" : "Live account"}</dd>
        <dt>Backend</dt><dd>{CLASSIC.replace(/^https?:\/\//, "")}</dd>
        <dt>Status</dt><dd><PMSyncIndicator status={state as any} /></dd>
        <dt>Last synced</dt><dd>{mode === "demo" ? "—" : s.lastSyncedAt ? relTime(s.lastSyncedAt.toISOString()) : "Not yet"}</dd>
      </dl>
      <p className="hint">Field data syncs to this browser the same way it syncs to the phone app, so reads work offline.</p>
    </PMCard>
  );
}

function About() {
  return (
    <PMCard eyebrow="About">
      <dl className="set-dl">
        <dt>App</dt><dd>PropelMapper v2 · {import.meta.env.VITE_APP_VERSION ?? "v2-dev"}</dd>
        <dt>Design system</dt><dd><code>{dsVersion.trim().slice(0, 7)}</code></dd>
      </dl>
    </PMCard>
  );
}
